import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle, CheckCircle2, ChevronDown, Clock, ClipboardList, FileStack, Hourglass, Play, XCircle,
} from 'lucide-react';
import { ScreenLink as Link } from '../permissions/ScreenLink';
import { api } from '../../services/api';
import Badge from '../ui/Badge';
import MenuButton from '../table/MenuButton';
import OverflowMenu from '../details/OverflowMenu';
import ReasonModal from '../sales/ReasonModal';
import { apiErrorMessage, formatIls, useDateFormatter } from '../sales/shared';
import { useStartTimer } from '../home/useHomeData';
import { WORK_TYPE_ORDER } from './CustomerWorkTypes';
import { toast } from '../../store/toastStore';
import type { Characterization, CharacterizationPayment, EngagementType } from '../../types';
import { advanceUnpaid, characterizationAnchor, chargeAwaitsApproval, chargeStillLocked, hoursMinutes } from './characterizationModel';
import PendingDecision from '../ui/PendingDecision';
import LeadStageBar from '../leads/LeadStageBar';
import CharacterizationPackagePanel from '../documentsReview/CharacterizationPackagePanel';

const STATUS_TONE = { active: 'info', completed: 'success', cancelled: 'neutral' } as const;

/**
 * P-07 (D-50, D-74) — the "אפיון" card of a customer, at the top of the
 * "אפיונים והצעות" tab.
 *
 *   ┌──────────────────────────────────────────────────────────────────┐
 *   │ אפיון — אתר חדש                 בתהליך · 12:30 ש' דווחו   [▶]    │
 *   │ תשלום 1 מתוך 2  ₪5,000 לפני מע"מ  שולם 02/10                     │
 *   │ תשלום 2 מתוך 2  ₪5,000 לפני מע"מ  ממתין                          │
 *   │ הערות: ...                                     קבצים          ⋯  │
 *   │ [סיום אפיון]   [פתיחת התקשרות מהאפיון ▾]                         │
 *   └──────────────────────────────────────────────────────────────────┘
 *
 * Amounts are BEFORE VAT and say so (D-74 §4). "Paid" is marked by the
 * General Manager / the Account Manager (`canWrite`), audited on the server
 * (D-74 §3). No block before installment 1 is paid — a warning only (§7).
 * "פתיחת התקשרות מהאפיון ▾" opens the FULL form of the chosen type (the
 * parent renders it with `characterizationId`); the server links the card in
 * the creating transaction (§8).
 */
export default function CharacterizationCard({
  card,
  orgId,
  canWrite,
  onOpenEngagement,
  onOpenDocuments,
}: {
  card: Characterization;
  orgId: string;
  /** GM / Account Manager on an active customer (the hub summary). */
  canWrite: boolean;
  onOpenEngagement: (type: EngagementType, card: Characterization) => void;
  onOpenDocuments: () => void;
}) {
  const { t } = useTranslation();
  const fmtDate = useDateFormatter();
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<null | 'complete' | 'cancel' | { pay: 1 | 2; paid: boolean }>(null);

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['characterizations', orgId] });
    queryClient.invalidateQueries({ queryKey: ['customer-hub-summary', orgId] });
    queryClient.invalidateQueries({ queryKey: ['organization', orgId] });
    queryClient.invalidateQueries({ queryKey: ['organizations'] });
  };
  const action = useMutation({
    mutationFn: async (reason: string) => {
      if (dialog === 'complete') return api.completeCharacterization(card.id);
      if (dialog === 'cancel') return api.cancelCharacterization(card.id, reason);
      if (dialog && typeof dialog === 'object') return api.setCharacterizationPayment(card.id, dialog.pay, dialog.paid, reason || undefined);
      return null;
    },
    onSuccess: () => {
      setDialog(null);
      refresh();
    },
  });
  const releaseLock = useMutation({
    mutationFn: (index: 1 | 2) => api.releaseCharacterizationPaymentLock(card.id, index),
    onSuccess: () => { toast('success', t('characterization.lockReleased')); refresh(); },
    onError: (err) => toast('error', apiErrorMessage(err, t('characterization.actionError'))),
  });
  const startTimer = useStartTimer({ onStarted: refresh });

  const active = card.status === 'active';
  const linked = card.engagement;
  const unpaidAdvance = advanceUnpaid(card);
  const statusLabel = t(`characterization.status.${card.status}`);
  const engagementHref = linked
    ? (linked.project_id && (linked.type === 'project' || linked.type === 'development') ? `/projects/${linked.project_id}` : `/engagements/${linked.id}`)
    : null;

  const paymentRow = (p: CharacterizationPayment) => {
    const paid = p.status === 'paid';
    return (
      <li key={p.installment_index} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
        <span className="text-surface-700">
          {t('characterization.payment', { index: p.installment_index, title: card.title })}
        </span>
        <span className="tabular-nums font-medium text-surface-900">
          {p.amount == null ? '—' : formatIls(p.amount)}
          {p.amount != null && <span className="ms-1 text-xs font-normal text-surface-600">{t('characterization.beforeVat')}</span>}
        </span>
        {paid ? (
          <Badge tone="success" dot>
            <CheckCircle2 size={12} aria-hidden="true" className="me-0.5" />
            {p.paid_at ? t('characterization.paidOn', { date: fmtDate(p.paid_at) }) : t('characterization.paid')}
          </Badge>
        ) : (
          <Badge tone="warning" dot>
            <Hourglass size={12} aria-hidden="true" className="me-0.5" />
            {t('characterization.pending')}
          </Badge>
        )}
        {canWrite && card.status !== 'cancelled' && (
          <button
            type="button"
            className="btn btn-ghost btn-sm text-xs"
            disabled={chargeAwaitsApproval(p)}
            aria-describedby={chargeAwaitsApproval(p) ? `${card.id}-pay-${p.installment_index}-wait` : undefined}
            onClick={() => { action.reset(); setDialog({ pay: p.installment_index, paid: !paid }); }}
          >
            {paid ? t('characterization.markUnpaid') : t('characterization.markPaid')}
          </button>
        )}
        {/* D-262 (QX-1243): the lock stays after "שולם" is cancelled - the manager releases it by hand */}
        {canWrite && card.status !== 'cancelled' && chargeStillLocked(p) && (
          <span className="flex flex-wrap items-center gap-x-2 text-xs text-warning-800">
            {t('characterization.stillLocked')}
            <button type="button" className="btn btn-ghost btn-sm text-xs" disabled={releaseLock.isPending} onClick={() => releaseLock.mutate(p.installment_index)}>
              {t('characterization.releaseLock')}
            </button>
          </span>
        )}
        {/* D-143: "שולם" waits for the OTE manager's approval of the charge */}
        {canWrite && card.status !== 'cancelled' && chargeAwaitsApproval(p) && (
          <span id={`${card.id}-pay-${p.installment_index}-wait`} className="text-xs text-surface-600">
            {t('characterization.paidAfterApproval')}
          </span>
        )}
      </li>
    );
  };

  const menuItems = canWrite && active
    ? [{
      key: 'cancel',
      label: t('characterization.cancel'),
      icon: <XCircle size={16} />,
      danger: true,
      onSelect: () => { action.reset(); setDialog('cancel'); },
    }]
    : [];

  return (
    <article
      id={characterizationAnchor(card.id)}
      className="rounded-xl border border-surface-200 bg-surface-0 p-4 space-y-3 scroll-mt-24"
      aria-label={t('characterization.cardTitle', { title: card.title })}
    >
      <header className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="flex items-center gap-2 text-base font-semibold text-surface-900">
            <ClipboardList size={18} className="text-primary-700 flex-shrink-0" aria-hidden="true" />
            <span className="break-words">{t('characterization.cardTitle', { title: card.title })}</span>
          </h3>
          <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-surface-600">
            <Badge tone={STATUS_TONE[card.status]} dot>{statusLabel}</Badge>
            <span className="inline-flex items-center gap-1 tabular-nums">
              <Clock size={12} aria-hidden="true" />
              {t('characterization.hoursReported', { hours: hoursMinutes(card.minutes) })}
            </span>
            {card.price != null && (
              <span className="tabular-nums">
                {t('characterization.price')}: {formatIls(card.price)} {t('characterization.beforeVat')}
              </span>
            )}
            {card.quote_id && card.quote_number != null && (
              <Link to={`/quotes/${card.quote_id}`} className="hover:underline underline-offset-2">
                {t('characterization.fromQuote', { number: card.quote_number })}
              </Link>
            )}
          </p>
        </div>
        <div className="flex items-center gap-1.5">
          {active && card.task_id && (
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              disabled={startTimer.isPending}
              aria-busy={startTimer.isPending || undefined}
              onClick={() => startTimer.mutate({ id: card.task_id!, title: t('characterization.cardTitle', { title: card.title }) })}
              title={t('characterization.startWork')}
            >
              <Play size={14} aria-hidden="true" />
              <span className="sr-only sm:not-sr-only">{t('characterization.startWork')}</span>
            </button>
          )}
          {menuItems.length > 0 && <OverflowMenu label={t('uxDetails.moreActions')} items={menuItems} />}
        </div>
      </header>

      {/* P-51 (D-224 s.1): the lead's stage bar, from the conversion point on (steps 1-4 done).
          FIX-B28 (QX-522): only on a card that came from a lead. */}
      {card.status !== 'cancelled' && card.lead_id && card.journey ? (
        <LeadStageBar
          className="rounded-lg bg-surface-50 px-3 py-2.5"
          facts={{
            lead_status: 'converted',
            converted_to_org_id: card.organization_id,
            characterization_meeting: card.journey.characterization_meeting,
            build_quote: card.journey.build_quote,
          }}
        />
      ) : null}

      <div className="flex items-start gap-2">
        <ul className="min-w-0 flex-1 space-y-1.5">{[...card.payments].sort((a, b) => a.installment_index - b.installment_index).map(paymentRow)}</ul>
        {/* D-125 PM-04: characterization revenue by payment month vs actually paid */}
        <PendingDecision id="PM-04" />
      </div>

      {unpaidAdvance && (
        <p className="flex items-start gap-2 rounded-lg bg-warning-50 px-3 py-2 text-xs text-warning-900" role="status">
          <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" aria-hidden="true" />
          {t('characterization.unpaidWarning')}
        </p>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
        <p className="min-w-0 text-surface-700 whitespace-pre-line break-words">
          <span className="text-surface-600">{t('characterization.notes')}: </span>
          {card.notes || '—'}
        </p>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onOpenDocuments}>
          <FileStack size={14} aria-hidden="true" /> {t('characterization.files')}
        </button>
      </div>

      {card.status === 'cancelled' && card.cancel_reason && (
        <p className="text-xs text-surface-600">{t('characterization.cancelledReason', { reason: card.cancel_reason })}</p>
      )}

      {(linked || (canWrite && card.status !== 'cancelled')) && (
        <footer className="flex flex-wrap items-center gap-2 border-t border-surface-100 pt-3">
          {canWrite && active && (
            <button type="button" className="btn btn-secondary" onClick={() => { action.reset(); setDialog('complete'); }}>
              <CheckCircle2 size={16} aria-hidden="true" /> {t('characterization.complete')}
            </button>
          )}
          {linked ? (
            <span className="text-sm text-surface-700">
              {t('characterization.linkedTo', {
                type: linked.type === 'service_without_contract' ? t('organization.tabLll') : t(`engagementDetail.types.${linked.type}`),
                name: linked.name ?? '—',
              })}
              {engagementHref && (
                <Link to={engagementHref} className="ms-2 font-medium text-primary-700 hover:underline underline-offset-2">
                  {t('characterization.openLinked')}
                </Link>
              )}
            </span>
          ) : canWrite && card.status !== 'cancelled' && (
            <MenuButton
              label={t('characterization.openEngagement')}
              align="start"
              popoverClassName="w-56 p-1.5"
              triggerClassName="btn btn-primary"
              items={WORK_TYPE_ORDER.map((ty) => ({
                key: ty,
                label: ty === 'service_without_contract' ? t('organization.tabLll') : t(`engagementDetail.types.${ty}`),
                onSelect: () => {
                  if (active) toast('info', t('characterization.stillOpen'));
                  onOpenEngagement(ty, card);
                },
              }))}
            >
              {t('characterization.openEngagement')}
              <ChevronDown size={16} strokeWidth={1.75} aria-hidden="true" />
            </MenuButton>
          )}
        </footer>
      )}

      {/* FIX-D337 (D-337 §6, §8): "פגישות אפיון" + "סיימנו את האפיון: להכין מסמכים" + "חבילת המסמכים". */}
      {card.status !== 'cancelled' && (
        <CharacterizationPackagePanel characterizationId={card.id} orgId={orgId} canWrite={canWrite} />
      )}

      {dialog === 'complete' && (
        <ReasonModal
          title={t('characterization.complete')}
          description={t('characterization.completeConfirm')}
          confirmLabel={t('characterization.complete')}
          loading={action.isPending}
          error={action.isError ? apiErrorMessage(action.error, t('characterization.actionError')) : null}
          onConfirm={(r) => action.mutate(r)}
          onCancel={() => setDialog(null)}
        />
      )}
      {dialog === 'cancel' && (
        <ReasonModal
          title={t('characterization.cancel')}
          description={t('characterization.cancelConfirm')}
          confirmLabel={t('characterization.cancel')}
          reasonLabel={t('characterization.cancelReason')}
          reasonRequired
          minLength={2}
          danger
          loading={action.isPending}
          error={action.isError ? apiErrorMessage(action.error, t('characterization.actionError')) : null}
          onConfirm={(r) => action.mutate(r)}
          onCancel={() => setDialog(null)}
        />
      )}
      {dialog && typeof dialog === 'object' && (
        <ReasonModal
          title={dialog.paid
            ? t('characterization.markPaidTitle', { index: dialog.pay })
            : t('characterization.markUnpaidTitle', { index: dialog.pay })}
          description={t('characterization.paymentAudit')}
          confirmLabel={dialog.paid ? t('characterization.markPaid') : t('characterization.markUnpaid')}
          reasonLabel={t('characterization.noteOptional')}
          loading={action.isPending}
          error={action.isError ? apiErrorMessage(action.error, t('characterization.actionError')) : null}
          onConfirm={(r) => action.mutate(r)}
          onCancel={() => setDialog(null)}
        />
      )}
    </article>
  );
}
